'use client';

import { c as _c } from "react/compiler-runtime";
import type { MotionValue, Transition } from 'framer-motion';
import { motion, useMotionValue, useReducedMotion, useTransform } from 'framer-motion';
import { X } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { useEffect, useEffectEvent, useState } from 'react';
import { DialogClose, DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/**
 * A short film that introduces a feature: a miniature of the page it lives on plays a few scenes,
 * each with a caption, over progress bars that also jump between scenes. The PR page's onboarding
 * and the auto-fix promo play one.
 */
export type FilmScene<Cue extends string> = {
  /** Names the scene apart from its caption, which can be rich text. */
  id: string;
  /** What its progress bar, which replays it, tells screen readers it shows. */
  label?: string;
  title: ReactNode;
  description: ReactNode;
  durationMs: number;
  /** Moments in the scene the miniature animates into, by milliseconds from its start. */
  cues: readonly {
    atMs: number;
    cue: Cue;
  }[];
};
export type FilmScenes<Cue extends string> = readonly [FilmScene<Cue>, ...FilmScene<Cue>[]];

/**
 * Where a cue stands at the film's position. The miniature animates into a `live` cue, reached
 * while its scene plays, and snaps to a `settled` one, which belongs to an earlier scene or to a
 * film that holds still.
 */
export type CuePhase = 'later' | 'pending' | 'live' | 'settled';
export type FilmFrame<Cue extends string> = {
  phase: (cue: Cue) => CuePhase;
  /** Milliseconds into the playing scene. Typed text derives from it. */
  elapsed: MotionValue<number>;
  /** Reduced motion: nothing loops. */
  still: boolean;
};
export const FILM_EASE = [0.25, 0.1, 0.25, 1] as const;
const SNAP: Transition = {
  duration: 0
};
export const isCueShown = (phase: CuePhase) => phase === 'live' || phase === 'settled';
/** Animates into a cue as it plays, and snaps to one the film is already past. */
export const cueTransition = (phase: CuePhase, transition: Transition) => phase === 'live' ? transition : SNAP;

/**
 * Where an element sits inside a miniature's drawing, for a line drawn between two of its parts.
 * Layout offsets ignore transforms, so the line lands right even while the coupons file glides,
 * and while the stage scales the drawing down to fit.
 */
export function offsetWithin(root: HTMLElement, element: HTMLElement) {
  let x = element.offsetLeft;
  let y = element.offsetTop;
  for (let parent = element.offsetParent; parent instanceof HTMLElement && parent !== root; parent = parent.offsetParent) {
    x += parent.offsetLeft + parent.clientLeft;
    y += parent.offsetTop + parent.clientTop;
  }
  return {
    x,
    y
  };
}

// requestAnimationFrame stops in a hidden tab. Capping each step makes the film pause there
// instead of skipping ahead when the tab comes back.
const MAX_STEP_MS = 100;
type FilmPosition = {
  scene: number;
  /** The latest cue time the clock has passed in this scene. */
  passedMs: number;
  /** Counts jumps, so clicking the playing scene's bar restarts it. */
  take: number;
};
function sceneAt<Cue extends string>(scenes: FilmScenes<Cue>, index: number) {
  return scenes[index] ?? scenes[0];
}
function cuePhase<Cue extends string>(scenes: FilmScenes<Cue>, {
  scene,
  passedMs
}: FilmPosition, still: boolean, cue: Cue): CuePhase {
  const owner = scenes.findIndex(entry => entry.cues.some(item => item.cue === cue));
  if (owner < scene) {
    return 'settled';
  }
  if (owner > scene) {
    return 'later';
  }
  if (still) {
    return 'settled';
  }
  return sceneAt(scenes, scene).cues.some(item => item.cue === cue && item.atMs <= passedMs) ? 'live' : 'pending';
}
function useFilmClock(scenes, still) {
  const $ = _c(23);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      scene: 0,
      passedMs: 0,
      take: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [position, setPosition] = useState(t0);
  const elapsed = useMotionValue(0);
  let t1;
  if ($[1] !== elapsed) {
    t1 = scene => {
      elapsed.set(0);
      setPosition(current => ({
        scene,
        passedMs: 0,
        take: current.take + 1
      }));
    };
    $[1] = elapsed;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const play = t1;
  let t2;
  if ($[3] !== play || $[4] !== position.scene || $[5] !== scenes.length) {
    t2 = () => play((position.scene + 1) % scenes.length);
    $[3] = play;
    $[4] = position.scene;
    $[5] = scenes.length;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const playNext = useEffectEvent(t2);
  let t3;
  if ($[7] !== elapsed || $[8] !== playNext || $[9] !== position.scene || $[10] !== scenes || $[11] !== still) {
    t3 = () => {
      if (still) {
        return;
      }
      const {
        cues,
        durationMs
      } = sceneAt(scenes, position.scene);
      let ms = 0;
      let passedMs = 0;
      let last = performance.now();
      let frame = 0;
      const tick = () => {
        const now = performance.now();
        ms = ms + Math.min(now - last, MAX_STEP_MS);
        ms;
        last = now;
        if (ms >= durationMs) {
          playNext();
          return;
        }
        elapsed.set(ms);
        const passed = Math.max(0, ...cues.map(_temp).filter(atMs => atMs <= ms));
        if (passed !== passedMs) {
          passedMs = passed;
          setPosition(current_0 => ({
            ...current_0,
            passedMs: passed
          }));
        }
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(frame);
    };
    $[7] = elapsed;
    $[8] = playNext;
    $[9] = position.scene;
    $[10] = scenes;
    $[11] = still;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== elapsed || $[14] !== position.scene || $[15] !== position.take || $[16] !== scenes || $[17] !== still) {
    t4 = [scenes, position.scene, position.take, still, elapsed];
    $[13] = elapsed;
    $[14] = position.scene;
    $[15] = position.take;
    $[16] = scenes;
    $[17] = still;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  useEffect(t3, t4);
  let t5;
  if ($[19] !== elapsed || $[20] !== play || $[21] !== position) {
    t5 = {
      position,
      elapsed,
      play
    };
    $[19] = elapsed;
    $[20] = play;
    $[21] = position;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
}
function _temp(item) {
  return item.atMs;
}
function SceneBars(t0) {
  const $ = _c(19);
  const {
    scenes,
    position,
    elapsed,
    still,
    onPlay
  } = t0;
  let t1;
  if ($[0] !== position.scene || $[1] !== scenes) {
    t1 = ms => ms / sceneAt(scenes, position.scene).durationMs;
    $[0] = position.scene;
    $[1] = scenes;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const progress = useTransform(elapsed, t1);
  let t2;
  if ($[3] !== position.scene || $[4] !== progress || $[5] !== still) {
    t2 = index => {
      if (index !== position.scene) {
        return index < position.scene ? 1 : 0;
      }
      return still ? 1 : progress;
    };
    $[3] = position.scene;
    $[4] = progress;
    $[5] = still;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const fill = t2;
  let t3;
  if ($[7] !== fill || $[8] !== onPlay || $[9] !== position.scene || $[10] !== scenes) {
    let t4;
    if ($[12] !== fill || $[13] !== onPlay || $[14] !== position.scene || $[15] !== scenes.length) {
      t4 = (scene, index_0) => <button key={scene.id} type="button" aria-label={`Step ${index_0 + 1} of ${scenes.length}${scene.label ? `: ${scene.label}` : ""}`} aria-current={index_0 === position.scene ? "step" : undefined} onClick={() => onPlay(index_0)} className="flex h-4 flex-1 cursor-pointer items-center rounded-sm focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"><span className="block h-[3px] w-full overflow-hidden rounded-[2px] bg-[#26262b]"><motion.span className="block size-full origin-left bg-zinc-200" style={{
            scaleX: fill(index_0)
          }} /></span></button>;
      $[12] = fill;
      $[13] = onPlay;
      $[14] = position.scene;
      $[15] = scenes.length;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    t3 = scenes.map(t4);
    $[7] = fill;
    $[8] = onPlay;
    $[9] = position.scene;
    $[10] = scenes;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[17] !== t3) {
    t4 = <div className="flex flex-1 gap-1.5">{t3}</div>;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
}
function Captions(t0) {
  const $ = _c(9);
  const {
    scenes,
    scene,
    still
  } = t0;
  let t1;
  if ($[0] !== scene || $[1] !== scenes || $[2] !== still) {
    let t2;
    if ($[4] !== scene || $[5] !== still) {
      t2 = (entry, index) => <motion.div key={entry.id} aria-hidden={index !== scene} initial={false} animate={index === scene ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 6
      }} transition={still ? {
        duration: 0
      } : {
        duration: 0.35,
        ease: "easeOut"
      }} className="col-start-1 row-start-1"><div className="text-[17px] font-semibold leading-[1.3] tracking-[-0.01em] text-zinc-50">{entry.title}</div><div className="mt-1.5 max-w-[54ch] text-sm leading-normal text-zinc-400">{entry.description}</div></motion.div>;
      $[4] = scene;
      $[5] = still;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = scenes.map(t2);
    $[0] = scene;
    $[1] = scenes;
    $[2] = still;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <div className="grid min-h-[92px] px-6 pt-[18px]">{t1}</div>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}

/**
 * The film inside its dialog: the miniature with the dialog's close button over it, the playing
 * scene's caption, and the scene bars beside the dialog's action. Mount it only while the dialog
 * is open, so the clock stops once it closes.
 */
export function OnboardingFilm(t0) {
  const $ = _c(29);
  const {
    scenes,
    miniature,
    action
  } = t0;
  const still = useReducedMotion() === true;
  const {
    position,
    elapsed,
    play
  } = useFilmClock(scenes, still);
  let t1;
  if ($[0] !== position || $[1] !== scenes || $[2] !== still) {
    t1 = cue => cuePhase(scenes, position, still, cue);
    $[0] = position;
    $[1] = scenes;
    $[2] = still;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== elapsed || $[5] !== miniature || $[6] !== still || $[7] !== t1) {
    t2 = miniature({
      phase: t1,
      elapsed,
      still
    });
    $[4] = elapsed;
    $[5] = miniature;
    $[6] = still;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DialogClose className="absolute right-2 top-2 grid size-[26px] place-items-center rounded-[7px] border border-[#26262b] bg-[#0b0b0d]/85 text-[#8b8b93] hover:bg-[#1c1c20] hover:text-zinc-200"><X className="size-3.5" aria-hidden="true" /><span className="sr-only">Close</span></DialogClose>;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2) {
    t4 = <div className="relative">{t2}{t3}</div>;
    $[10] = t2;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== position.scene || $[13] !== scenes || $[14] !== still) {
    t5 = <Captions scenes={scenes} scene={position.scene} still={still} />;
    $[12] = position.scene;
    $[13] = scenes;
    $[14] = still;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== elapsed || $[17] !== play || $[18] !== position || $[19] !== scenes || $[20] !== still) {
    t6 = <SceneBars scenes={scenes} position={position} elapsed={elapsed} still={still} onPlay={play} />;
    $[16] = elapsed;
    $[17] = play;
    $[18] = position;
    $[19] = scenes;
    $[20] = still;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== action || $[23] !== t6) {
    t7 = <div className="flex items-center gap-[18px] px-6 pb-5 pt-2.5">{t6}{action}</div>;
    $[22] = action;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t4 || $[26] !== t5 || $[27] !== t7) {
    t8 = <>{t4}{t5}{t7}</>;
    $[25] = t4;
    $[26] = t5;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
}

/** The dialog an onboarding film plays in, without the dialog's own close button. */
export function OnboardingFilmDialogContent(t0) {
  const $ = _c(12);
  let className;
  let overlayClassName;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      overlayClassName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = overlayClassName;
    $[3] = props;
  } else {
    className = $[1];
    overlayClassName = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("max-h-[calc(85dvh-1rem)] w-[min(680px,calc(100vw-2rem))] max-w-none gap-0 overflow-y-auto rounded-[14px] border-[#26262b] bg-[#0b0b0d] p-0 text-zinc-50 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)] motion-reduce:transition-none! sm:rounded-[14px] [&>[data-dialog-close]]:hidden", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== overlayClassName) {
    t2 = cn("motion-reduce:animate-none!", overlayClassName);
    $[6] = overlayClassName;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== t1 || $[10] !== t2) {
    t3 = <DialogContent className={t1} overlayClassName={t2} {...props} />;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}